import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import type { ChartOptions } from "chart.js";
import { useEffect, useMemo, useState } from "react";
import { fetchPreventiveMaintenanceStatus, getEmployeeTargets } from "./services/api";
import "./App.css";

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

const performers = ["Deepak", "Sresh", "Gaurav", "Ishwar"];
const completionData = [85, 75, 68, 58];

type EmployeeCompletedCount = {
    employee_id: number;
    employee_name: string;
    completed: number;
    pending?: number;
    target?: number;
};

export default function EngineerDashboard() {
    const [pmStatuses, setPmStatuses] = useState<any[]>([]);
    const [employeeTargets, setEmployeeTargets] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const formatDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const now = new Date();

    // First day of last month
    const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    // Last day of last month
    const endDate = new Date(now.getFullYear(), now.getMonth(), 0);

    const startDateStr = formatDate(startDate);
    const endDateStr = formatDate(endDate);

    useEffect(() => {
        handleFetchClick();
    }, []);

    function handleFetchClick(): void {
        setLoading(true);
        setError(null);
        setPmStatuses([]);
        Promise.all([
            fetchPreventiveMaintenanceStatus(startDateStr, endDateStr),
            getEmployeeTargets(""),
        ])
            .then(([response, targets]) => {
                setPmStatuses(response);
                setEmployeeTargets(targets);
            })
            .catch((error: any) => {
                console.error('Error fetching engineer task data:', error);
                setError('Failed to fetch engineer task data');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const getEmployeeWiseCompletedCount = (
        data: any[]
    ): EmployeeCompletedCount[] => {
        const map: Record<number, EmployeeCompletedCount> = {};

        data.forEach(item => {
            if (item.status === 'COMPLETE') {
                if (!map[item.employee_id]) {
                    map[item.employee_id] = {
                        employee_id: item.employee_id,
                        employee_name: item.employee_name,
                        completed: 0
                    };
                }
                map[item.employee_id].completed += 1;
            } else {
                if (!map[item.employee_id]) {
                    map[item.employee_id] = {
                        employee_id: item.employee_id,
                        employee_name: item.employee_name,
                        completed: 0
                    };
                }
                if (map[item.employee_id].pending) {
                    map[item.employee_id].pending! += 1;
                } else {
                    map[item.employee_id].pending = 1;
                }
            }
            const target = employeeTargets.find(target => target.employee_id === item.employee_id)?.target_value || 0;
            const pendingCount = target - map[item.employee_id].completed || 0;
            map[item.employee_id].pending = pendingCount;
            map[item.employee_id].target = target;
        });
        console.log("Employee Completed Map:", map);
        return Object.values(map);
    };

    const employeeCompleted = useMemo(
        () => getEmployeeWiseCompletedCount(pmStatuses),
        [pmStatuses, employeeTargets]
    );

    const top5 = [...employeeCompleted]
        .sort((a, b) => b.completed - a.completed)
        .slice(0, 5);

    const totals = employeeCompleted.reduce(
        (acc, item) => {
            acc.completed += item.completed;
            acc.pending += item.pending ?? 0; // handle undefined
            return acc;
        },
        { completed: 0, pending: 0 }
    );

    const chartData = {
        labels: top5.map(e => e.employee_name),
        datasets: [
            {
                label: 'Completed',
                data: top5.map(e => e.completed),
                backgroundColor: [
                    'lightblue',

                ],
                borderRadius: 8,
                barThickness: 22,
            },
        ],
    };

    const barOptions: ChartOptions<"bar"> = {
        indexAxis: "y" as const,
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: true } },
            y: { grid: { display: true } },
        },
    };

    /* ---------------- DOUGHNUT ---------------- */
    const doughnutData = {
        labels: ["Completed", "Pending"],
        datasets: [
            {
                label: 'Completion vs. target',

                data: [totals.completed, totals.pending],
                backgroundColor: ["lightblue", "lightgray"],
                borderWidth: 0,
            },
        ],
    };



    const doughnutOptions: ChartOptions<"doughnut"> = {
        responsive: true,
        cutout: "50%", // donut thickness
        plugins: {
            legend: {
                position: "bottom" as const,
            },
            tooltip: {
                enabled: true,
            },
        },
    };

    if (loading) {
        return (
            <Container fluid className="p-4 p-md-5 d-flex align-items-center justify-content-center" style={{ minHeight: 400 }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" style={{ width: 3 + "rem", height: 3 + "rem" }} />
                    <p className="mt-3 text-muted mb-0">Loading engineer task data...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="p-4 p-md-5">
                <div className="text-center text-danger py-5">
                    <p className="mb-3">{error}</p>
                    <button type="button" className="btn btn-outline-primary" onClick={handleFetchClick}>
                        Retry
                    </button>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4 p-md-5">
            {/* ---------------- HEADER ---------------- */}
            <div className="text-center mb-4">
                <h2 className="fw-bold">Engineers Task Dashboard</h2>
                <p className="text-muted">
                    This dashboard highlights the top performers in task completion among
                    engineers and provides an overview of their performance-related
                    metrics
                </p>
            </div>

            {/* ---------------- TOP SECTION ---------------- */}
            <Row className="mb-4">
                {/* LEFT - BAR */}
                <Col md={9}>
                    <Card className="p-3">
                        <h5 className="mb-3">Top 5 Performer of the Month</h5>
                        <div style={{ height: 260 }}>
                            <Bar data={chartData} options={barOptions} />
                        </div>
                    </Card>
                </Col>

                {/* RIGHT - DOUGHNUT + CARDS */}
                <Col md={3}>
                    <Card className="p-3 mb-3 text-center h-100">
                        <h5>Target vs Achievement</h5>
                        <div style={{ position: "relative" }}>
                            <Doughnut data={doughnutData} options={doughnutOptions} />
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* ---------------- BOTTOM SECTION ---------------- */}
            <Row>
                <Col md={12}>
                    <Card className="p-3" style={{ height: "400px", width: "100%" }}>
                        <h5>Engineer performance</h5>
                        <Table striped bordered hover responsive className="mb-0">
                            <thead className="sticky-header">
                                <tr>
                                    <th>Engineer</th>
                                    <th>Target</th>
                                    <th>Completed</th>
                                    <th>Pending</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employeeCompleted.map((item) => (
                                    <tr key={item.employee_id}>
                                        <td>{item.employee_name}</td>
                                        <td>{item.target}</td>
                                        <td>{item.completed}</td>
                                        <td>{item.pending}</td>
                                    </tr>
                                ))}

                            </tbody>
                        </Table>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
